import type { FinancialAddress, FinancialShipmentResponse } from '@/types/Api/FinancialShipment'
import type {
  FinancialCharge,
  FinancialImport,
  FinancialMatch,
  FinancialMatchResult,
  FinancialReview,
} from '@/types/Models/Financial'
import { FINANCIAL_CARRIER_HANDLES } from '@/const/Financial'
import { financialDecimal } from '@/services/FinancialAmounts'
import { financialInvoiceLabel } from '@/services/FinancialImport'
import { financialShipmentIndex, financialShipmentKey } from '@/services/FinancialSignals'
import FinancialProxy from '@/proxies/FinancialProxy'

const dimension = (value: number | null | undefined): number | null =>
  typeof value === 'number' && value > 0 ? value : null

// Only fields that affect who is billed or how the charge is judged reopen a review.
const evidenceChanges = (previous: FinancialMatch, next: FinancialMatch): string[] => {
  const changes: string[] = []
  if (previous.itemId !== next.itemId) changes.push('a different PacSort shipment')
  if (previous.companyId !== next.companyId || previous.companyName !== next.companyName)
    changes.push(`customer ${previous.companyName} → ${next.companyName}`)
  if (previous.weightGrams !== next.weightGrams)
    changes.push(`booked weight ${previous.weightGrams ?? '—'} g → ${next.weightGrams ?? '—'} g`)
  if (previous.bookedPrice !== next.bookedPrice)
    changes.push(`booked price ${previous.bookedPrice ?? '—'} → ${next.bookedPrice ?? '—'}`)
  if (previous.service !== next.service) changes.push(`service ${previous.service} → ${next.service}`)
  if ((previous.billingId ?? previous.companyId) !== (next.billingId ?? next.companyId))
    changes.push(`billing customer ${previous.billingName ?? previous.companyName} → ${next.billingName ?? next.companyName}`)
  return changes
}

export const financialReopenReview = (review: FinancialReview, reason: string): FinancialReview => {
  return {
    ...review,
    status: 'pending',
    reason,
    warningsChecked: false,
    history: [
      ...(review.history ?? []),
      {
        status: review.status,
        customerPrice: review.customerPrice,
        purchasePrice: review.purchasePrice,
        note: review.note,
        userName: review.userName,
        reviewedAt: review.reviewedAt,
        reason: review.reason ?? '',
      },
    ],
  }
}

const addressLabel = (address: FinancialAddress): string =>
  [address.companyName || address.companyShortName, address.addressLine, address.city].filter(Boolean).join(', ') ||
  'Sender address'

const shiftDate = (value: string, days: number): string =>
  new Date(Date.parse(value.slice(0, 10)) + days * 86400000).toISOString().slice(0, 10)

// 21grams bills per company, and per sender address when a company ships from several addresses (like a marketplace
// with sub-customers). Sender addresses come from the existing assignment list, limited to the booking dates.
export const financialSenderAddresses = async (
  matches: FinancialMatch[],
  signal: AbortSignal,
): Promise<{ addresses: Map<string, FinancialAddress | null>; failed: boolean }> => {
  const addresses = new Map<string, FinancialAddress | null>()
  const companies = new Map<string, string[]>()
  for (const match of matches) companies.set(match.companyId, [...(companies.get(match.companyId) ?? []), match.createdAt])
  const proxy = new FinancialProxy()
  let failed = false
  for (const [companyId, dates] of companies) {
    const sorted = dates.map((date) => date.slice(0, 10)).sort()
    try {
      let page = 1
      let received = 0
      let count = 0
      do {
        signal.throwIfAborted()
        const response = await proxy.findAssignments(companyId, shiftDate(sorted[0], -1), shiftDate(sorted[sorted.length - 1], 1), page, signal)
        if (!Array.isArray(response.data)) throw new Error('Incomplete assignment response')
        for (const assignment of response.data) addresses.set(assignment.id, assignment.senderAddress ?? null)
        received += response.data.length
        count = response.count
        page += 1
      } while (received < count && page <= 200)
    } catch (error) {
      if (signal.aborted) throw error
      failed = true
    }
  }
  return { addresses, failed }
}

// Companies that shipped from more than one sender address are billed per address.
export const financialSplitCompanies = (matches: FinancialMatch[], addresses: Map<string, FinancialAddress | null>): Set<string> => {
  const perCompany = new Map<string, Set<string>>()
  for (const match of matches) {
    const address = match.assignmentId ? addresses.get(match.assignmentId) : null
    if (address) perCompany.set(match.companyId, new Set([...(perCompany.get(match.companyId) ?? []), address.id]))
  }
  return new Set([...perCompany].filter(([, ids]) => ids.size > 1).map(([companyId]) => companyId))
}

export const financialWithBilling = (match: FinancialMatch, addresses: Map<string, FinancialAddress | null>, split: Set<string>): FinancialMatch => {
  const address = match.assignmentId ? addresses.get(match.assignmentId) : undefined
  if (address === undefined) return { ...match, billingId: match.billingId ?? match.companyId, billingName: match.billingName ?? match.companyName }
  const label = address ? addressLabel(address) : null
  const perAddress = split.has(match.companyId) && !!address
  return {
    ...match,
    senderAddressId: address?.id ?? null,
    senderAddressName: label,
    billingId: perAddress ? `${match.companyId}:${address!.id}` : match.companyId,
    billingName: perAddress ? `${match.companyName} · ${label}` : match.companyName,
  }
}

export const matchFinancialImport = async (
  batch: FinancialImport,
  signal: AbortSignal,
  onProgress: (completed: number, total: number) => void,
  others: FinancialImport[] = [],
  knownSplit: Set<string> = new Set(),
): Promise<FinancialMatchResult> => {
  const numbers = [...new Set(batch.charges.map((row) => row.trackingNumber).filter(Boolean))]
  const matches = new Map<string, Map<string, FinancialMatch>>()
  const proxy = new FinancialProxy()
  for (let offset = 0; offset < numbers.length; offset += 80) {
    const chunk = numbers.slice(offset, offset + 80)
    let page = 1
    let received = 0
    let count: number
    do {
      signal.throwIfAborted()
      const response = await proxy.findShipments(chunk, page, signal)
      if (!Array.isArray(response.data) || !Number.isInteger(response.count) || response.count < 0)
        throw new Error('PacSort returned an incomplete shipment response. Retry matching.')
      received += response.data.length
      count = response.count
      if (page > 100 || (!response.data.length && received < count))
        throw new Error('Shipment lookup could not be completed. No matching changes have been saved.')
      for (const item of response.data) collectMatches(item, batch, chunk, matches)
      page += 1
    } while (received < count)
    onProgress(Math.min(offset + chunk.length, numbers.length), numbers.length)
  }
  const found = [...matches.values()].flatMap((candidates) => [...candidates.values()])
  const { addresses, failed } = await financialSenderAddresses(found, signal)
  const split = new Set([...financialSplitCompanies(found, addresses), ...knownSplit])
  for (const candidates of matches.values())
    for (const [id, match] of candidates) candidates.set(id, financialWithBilling(match, addresses, split))
  // The shipment search omits archived PacSort items, so later fees fall back to earlier invoices of the same carrier.
  const earlier = financialShipmentIndex(others.filter((other) => other.id !== batch.id && other.carrier === batch.carrier))
  const matchedAt = new Date().toISOString()
  let reopened = 0
  let retained = 0
  let linked = 0
  const charges = batch.charges.map((row): FinancialCharge => {
    const candidates = [...(matches.get(row.trackingNumber)?.values() ?? [])]
    let match: FinancialMatch | null = candidates.length === 1 ? candidates[0] : null
    if (row.trackingNumber && !candidates.length) {
      if (row.match) {
        match = {
          ...row.match,
          origin: 'retained',
          originNote: `PacSort no longer returns this shipment (it may be archived). The match from ${(batch.matchedAt ?? matchedAt).slice(0, 10)} is kept.`,
        }
        retained += 1
      } else {
        const source = (earlier.get(financialShipmentKey(batch.carrier, row.trackingNumber)) ?? []).find(
          (line) => line.row.match && line.row.matchStatus === 'matched',
        )
        if (source?.row.match) {
          match = {
            ...source.row.match,
            origin: 'workspace',
            originNote: `Linked through invoice ${financialInvoiceLabel(source.batch)}, row ${source.row.rowNumber}. PacSort no longer returns the shipment, for example because it is archived.`,
          }
          linked += 1
        }
      }
    }
    const matchStatus = !row.trackingNumber
      ? 'missing-id'
      : candidates.length > 1
        ? 'ambiguous'
        : match
          ? 'matched'
          : 'unmatched'
    let review = row.review
    if (review && review.status !== 'pending') {
      const changes = row.match && match ? evidenceChanges(row.match, match) : []
      const reason =
        matchStatus === 'ambiguous'
          ? 'Several PacSort shipments now share this number.'
          : !row.match && match
            ? 'A unique PacSort shipment was found for this row.'
            : changes.length
              ? `PacSort data changed: ${changes.join(', ')}.`
              : ''
      if (reason) {
        review = financialReopenReview(review, reason)
        reopened += 1
      }
    }
    // A grid change was a decision about the old PacSort data: it lapses when the shipment or its data changes.
    const lapsed =
      !!row.adjustment &&
      (matchStatus !== row.matchStatus ||
        row.match?.itemId !== match?.itemId ||
        (row.match && match ? evidenceChanges(row.match, match).length > 0 : false))
    const next: FinancialCharge = { ...row, match, matchStatus, review }
    if (lapsed) delete next.adjustment
    return next
  })
  return { batch: { ...batch, matchedAt, charges }, reopened, retained, linked, addressesFailed: failed }
}

export const financialMatchFromResponse = (item: FinancialShipmentResponse): FinancialMatch => {
  const dimensionsCm = { length: dimension(item.length), width: dimension(item.width), height: dimension(item.height) }
  return {
    itemId: item.id,
    itemReference: item.itemReference,
    assignmentReference: item.assignmentReference,
    assignmentId: item.assignmentId,
    companyId: item.company.id,
    companyName: item.company.name,
    // Core stores actualPrice as a double; read it at 14 significant digits like PHP, so 20.044999999999998 is 20.045.
    bookedPrice:
      item.actualPrice === null || item.actualPrice === undefined
        ? null
        : financialDecimal(typeof item.actualPrice === 'number' ? String(Number(item.actualPrice.toPrecision(14))) : String(item.actualPrice)),
    weightGrams: item.weight,
    service: item.serviceLevel,
    createdAt: item.createdAt,
    dimensionsCm: Object.values(dimensionsCm).some((value) => value !== null) ? dimensionsCm : null,
    receivedInFacilityAt: item.receivedInFacilityAt ?? null,
    origin: 'lookup',
  }
}

const collectMatches = (
  item: FinancialShipmentResponse,
  batch: FinancialImport,
  numbers: string[],
  matches: Map<string, Map<string, FinancialMatch>>,
): void => {
  if (!item.id || !item.company?.id || !Array.isArray(item.shipments)) return
  for (const shipment of item.shipments) {
    if (
      shipment.carrierHandle !== FINANCIAL_CARRIER_HANDLES[batch.carrier] ||
      !numbers.includes(shipment.shipmentNumber)
    )
      continue
    const candidates = matches.get(shipment.shipmentNumber) ?? new Map<string, FinancialMatch>()
    candidates.set(item.id, financialMatchFromResponse(item))
    matches.set(shipment.shipmentNumber, candidates)
  }
}
